import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";

import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import { getTokens, brandAlpha } from "../../styles/tokens";
import { isWebchat } from "../../utils/channels";
import {
	usePanelStyles,
	ModalTitle,
	FormSection,
	FieldLabel,
	SettingRow,
} from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
	const t = getTokens(theme.palette.type, theme.palette.primary.main);
	return {
		root: {
			display: "flex",
			flexWrap: "wrap",
		},
		// O Autocomplete é do MUI v5 e o campo é v4: sem isto ele sai mais alto que os vizinhos (40 px).
		userField: {
			"& .MuiOutlinedInput-root.MuiOutlinedInput-root": {
				paddingTop: 0,
				paddingBottom: 0,
				paddingLeft: 6,
			},
			"& .MuiAutocomplete-input.MuiAutocomplete-input": {
				padding: "10px 6px !important",
			},
		},
		/* campos extras: nome | valor | remover */
		extraGrid: {
			display: "grid",
			gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 34px",
			gap: "8px 12px",
			alignItems: "center",
			marginBottom: 12,
			"& > .MuiFormControl-root": { margin: 0 },
		},
		extraHead: {
			marginBottom: 0,
		},
		removeBtn: {
			width: 34,
			height: 34,
			padding: 0,
			color: t.textSecondary,
			"&:hover": {
				backgroundColor: brandAlpha(t.semantic.error, t.mode === "dark" ? 0.16 : 0.08),
				color: t.mode === "dark" ? t.status.down.fg : t.semantic.error,
			},
		},
		metaGrid: {
			marginTop: 14,
		},
		metaValue: {
			fontFamily: t.font,
			fontSize: 14,
			lineHeight: 1.5,
			color: t.text,
			overflowWrap: "anywhere",
		},
		saveBtn: {
			position: "relative",
		},
		buttonProgress: {
			color: "inherit",
			position: "absolute",
			top: "50%",
			left: "50%",
			marginTop: -10,
			marginLeft: -10,
		},
	};
});

const formatDateForInput = (date) => {
	if (!date) return "";

	// Se já está no formato YYYY-MM-DD, retorna como está
	if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date;
	}

	// Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
	const d = new Date(date);
	if (isNaN(d.getTime())) return "";

	// Usar métodos locais para evitar problemas de timezone
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
	if (!dateString) return null;

	// Se já está no formato YYYY-MM-DD, retorna como está
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return dateString;
	}

	// Se é uma data ISO, extrai apenas a parte da data
	if (dateString.includes("T")) {
		return dateString.split("T")[0];
	}

	return dateString;
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const p = usePanelStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		disableBot: false,
		lgpdAcceptedAt: "",
		birthDate: "",
	};

	const [contact, setContact] = useState(initialState);
	const [disableBot, setDisableBot] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open || searchParam.length < 3) {
			setLoading(false);
			setSelectedQueue("");
			return;
		}
		const delayDebounceFn = setTimeout(() => {
			setLoading(true);
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam },
					});
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact((prevState) => {
					return {
						...prevState,
						...initialValues,
						// Formatar a data corretamente
						birthDate: formatDateForInput(initialValues.birthDate),
					};
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...data,
						birthDate: formatDateForInput(data.birthDate),
					});
					setDisableBot(data.disableBot);

					// Preenche automaticamente os campos de Wallet e Queue
					if (data.contactWallets && data.contactWallets.length > 0) {
						const wallet = data.contactWallets[0].wallet;
						const queue = data.contactWallets[0].queue;

						setSelectedUser({
							id: wallet.id,
							name: wallet.name,
						});
						setSelectedQueue(queue.id);

						// Atualiza as filas disponíveis no campo de seleção
						setQueues([{ id: queue.id, name: queue.name }]);
					} else {
						// Limpa os valores quando não há contactWallets
						setSelectedUser(null);
						setSelectedQueue(null);
						setQueues([]);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	// Contato do webchat: o número é sintético ("wc-…") e não muda (o servidor também ignora).
	const isWebchatContact = isWebchat(contact?.channel);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setSelectedUser(null);
		setSelectedQueue(null);
		setQueues([]);
	};

	const handleSaveContact = async (values) => {
		try {
			// Preparar os dados com a data corretamente formatada
			const contactData = {
				...values,
				disableBot: disableBot,
				birthDate: parseDateFromInput(values.birthDate),
			};

			if (contactId) {
				if (!selectedUser && !selectedQueue) {
					delete contact.contactWallets;
					await api.delete(`/contacts/wallet/${contactId}`);
				}

				const { contactWallets, ...valuesWithoutWallets } = contactData;
				delete contact.contactWallets;

				await api.put(`/contacts/${contactId}`, contactData);

				if (selectedUser && selectedQueue && selectedUser !== null && selectedQueue !== null) {
					await api.put(`/contacts/wallet/${contactId}`, {
						wallets: {
							userId: selectedUser.id,
							queueId: selectedQueue,
						},
					});
				}

				handleClose();
			} else {
				delete contactData.contactWallets;

				const { data } = await api.post("/contacts", contactData);

				if (data.id && selectedUser && selectedQueue) {
					await api.put(`/contacts/wallet/${data.id}`, {
						wallets: {
							userId: selectedUser.id,
							queueId: selectedQueue,
						},
					});
				}

				if (onSave) {
					onSave(data);
				}

				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
				classes={{ paper: p.dialogPaper }}
			>
				<ModalTitle
					id="form-dialog-title"
					title={
						contactId
							? `${i18n.t("contactModal.title.edit")}`
							: `${i18n.t("contactModal.title.add")}`
					}
					subtitle="Dados, campos personalizados e carteira do contato."
					onClose={handleClose}
				/>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form className={p.modalForm}>
							<DialogContent className={clsx(p.modalBody, p.labelsAbove)}>
								{/* ---------- Seção: Dados ---------- */}
								<FormSection
									title={i18n.t("contactModal.form.mainInfo")}
									description="Nome, número e canais de contato." /* TODO i18n */
								>
									<div className={p.grid2}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.name")}
											name="name"
											autoFocus
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											size="small"
											fullWidth
											className={p.span2}
										/>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.number")}
											name="number"
											error={touched.number && Boolean(errors.number)}
											helperText={touched.number && errors.number}
											placeholder="5513912344321"
											variant="outlined"
											size="small"
											fullWidth
											{...(isWebchatContact ? { disabled: true, helperText: i18n.t("contactModal.form.webchatNumber") } : {})}
										/>
										<TextField
											label="LID (Linked Device ID)"
											value={contact.lid || ""}
											disabled
											fullWidth
											size="small"
											variant="outlined"
											helperText="Identificador do dispositivo vinculado"
										/>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.email")}
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											placeholder="email@exemplo.com"
											fullWidth
											size="small"
											variant="outlined"
										/>
										<Field
											as={TextField}
											label="Data de Nascimento"
											name="birthDate"
											type="date"
											InputLabelProps={{ shrink: true }}
											fullWidth
											size="small"
											variant="outlined"
											helperText="Usada nas mensagens automáticas de aniversário"
											onChange={(e) => {
												const formattedDate = parseDateFromInput(e.target.value);
												setFieldValue("birthDate", formattedDate);
											}}
										/>
										<FieldLabel label="Tags" className={p.span2}>
											<TagsContainer contact={contact} />
										</FieldLabel>
									</div>
								</FormSection>

								{/* ---------- Seção: Informações extras ---------- */}
								<FormSection
									title={i18n.t("contactModal.form.extraInfo")}
									description="Campos personalizados (ex.: CPF, plano, endereço)." /* TODO i18n */
								>
									<FieldArray name="extraInfo">
										{({ push, remove }) => (
											<>
												{values.extraInfo && values.extraInfo.length > 0 && (
													<div className={classes.extraGrid}>
														<span className={clsx(p.fieldLabel, classes.extraHead)}>
															{i18n.t("contactModal.form.extraName")}
														</span>
														<span className={clsx(p.fieldLabel, classes.extraHead)}>
															{i18n.t("contactModal.form.extraValue")}
														</span>
														<span />
														{values.extraInfo.map((info, index) => (
															<React.Fragment key={`${index}-info`}>
																<Field
																	as={TextField}
																	name={`extraInfo[${index}].name`}
																	placeholder={i18n.t("contactModal.form.extraName")}
																	inputProps={{ "aria-label": i18n.t("contactModal.form.extraName") }}
																	variant="outlined"
																	size="small"
																	fullWidth
																/>
																<Field
																	as={TextField}
																	name={`extraInfo[${index}].value`}
																	placeholder={i18n.t("contactModal.form.extraValue")}
																	inputProps={{ "aria-label": i18n.t("contactModal.form.extraValue") }}
																	variant="outlined"
																	size="small"
																	fullWidth
																/>
																<Tooltip title="Remover">
																	<IconButton
																		className={classes.removeBtn}
																		onClick={() => remove(index)}
																		aria-label="Remover informação"
																	>
																		<DeleteOutlineIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															</React.Fragment>
														))}
													</div>
												)}
												<Button
													variant="outlined"
													size="small"
													startIcon={<AddIcon fontSize="small" />}
													onClick={() => push({ name: "", value: "" })}
												>
													{i18n.t("contactModal.buttons.addExtraInfo")}
												</Button>
											</>
										)}
									</FieldArray>
								</FormSection>

								{/* ---------- Seção: Configurações ---------- */}
								<FormSection
									title="Configurações" /* TODO i18n */
									description={`${i18n.t("contactModal.form.assignWallet")}, chatbot e origem do contato.`} /* TODO i18n */
								>
									<div className={p.grid2}>
										<Autocomplete
											value={selectedUser}
											fullWidth
											getOptionLabel={(option) => `${option.name}`}
											onChange={(e, newValue) => {
												setSelectedUser(newValue);
												if (newValue != null && Array.isArray(newValue.queues)) {
													if (newValue.queues.length === 1) {
														setSelectedQueue(newValue.queues[0].id);
													}
													setQueues(newValue.queues);
												} else {
													setQueues(allQueues);
													setSelectedQueue(null);
													setSelectedUser(null);
												}
											}}
											options={options}
											filterOptions={(options, { inputValue }) =>
												options.filter((option) =>
													option.name.toLowerCase().includes(inputValue.toLowerCase())
												)
											}
											freeSolo
											autoHighlight
											noOptionsText={i18n.t("transferTicketModal.noOptions")}
											loading={loading}
											renderOption={(props, option) => (
												<li {...props}>
													<span style={{ marginLeft: 8 }}>{option.name}</span>
												</li>
											)}
											renderInput={(params) => (
												<TextField
													{...params}
													label={i18n.t("transferTicketModal.fieldLabel")}
													variant="outlined"
													size="small"
													className={classes.userField}
													onChange={(e) => setSearchParam(e.target.value)}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<React.Fragment>
																{loading ? <CircularProgress color="inherit" size={20} /> : null}
																{params.InputProps.endAdornment}
															</React.Fragment>
														),
													}}
												/>
											)}
										/>

										<FormControl variant="outlined" fullWidth size="small">
											<InputLabel>{i18n.t("transferTicketModal.fieldQueueLabel")}</InputLabel>
											<Select
												value={selectedQueue}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
											>
												{queues.map((queue) => (
													<MenuItem key={queue.id} value={queue.id}>
														{queue.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</div>

									<SettingRow
										label={i18n.t("contactModal.form.chatBotContact")}
										description="Quando ligado, o chatbot não responde a este contato."
									>
										<Switch
											color="primary"
											checked={disableBot}
											onChange={() => setDisableBot(!disableBot)}
											name="disableBot"
											inputProps={{ "aria-label": i18n.t("contactModal.form.chatBotContact") }}
										/>
									</SettingRow>

									<div className={clsx(p.grid2, classes.metaGrid)}>
										<FieldLabel label={i18n.t("contactModal.form.whatsapp")}>
											<span className={classes.metaValue}>
												{contact?.whatsapp ? contact?.whatsapp.name : "—"}
											</span>
										</FieldLabel>
										<FieldLabel label={i18n.t("contactModal.form.termsLGDP")}>
											<span className={classes.metaValue}>
												{contact?.lgpdAcceptedAt
													? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm")
													: "—"}
											</span>
										</FieldLabel>
									</div>
								</FormSection>
							</DialogContent>
							<DialogActions>
								<Button onClick={handleClose} disabled={isSubmitting}>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.saveBtn}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && <CircularProgress size={20} className={classes.buttonProgress} />}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
